import { auroraTheme } from '@vfg-source-web/theme-aurora';
import { useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { Banner, Footer, Header, JourneySelector } from './components';
import { ContentfulProvider, ThemeProvider } from './providers';
import { JourneyConfig, MarketSegmentEnum, SubscriptionTypeEnum } from './types';
import { getMarketSegment } from './utils';

import { ContentContainer, InnerContainer, MainContainer, MenuContainer } from './App.styles';

import ErrorFallback from './components/ErrorFallback';

import { useEvent } from '@vfmfe/foundations';
import JourneyLoader from './components/JourneyLoader';
import LoggedUserInfo from './components/LoggedUserInfo';
import withProvider from './hoc/withProvider';
import { useSelector } from './modules/store';
import NotificationsProvider from './providers/Notifications';

import { OverflowMenu, OverflowMenuItem } from '@uk-source-web/overflow-menu';
import CustomerTreeResponsiveTray from './components/CustomerTreeResponsiveTray';
import { useClickOutside } from './hooks/useClickOutside';
import LoadingProvider from './providers/Loading';
import useAuthentication from './hooks/useAuthentication';
import ErrorProvider from './providers/Error';

const AppContent = () => {
    const currentLoggedUser = useSelector((state) => state?.authorization?.currentLoggedUser);
    const isLoggedUser = !!currentLoggedUser?.username;
    const { handleLoginClick, handleLogoutClick } = useAuthentication();
    const [selectedSubscriptionType, setSelectedSubscriptionType] = useState<SubscriptionTypeEnum | undefined>(
        undefined
    );
    const [selectedJourney, setSelectedJourney] = useState<JourneyConfig | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const isAmigo = getMarketSegment() === MarketSegmentEnum.AMIGO;

    const renderSelector = () => {
        return <JourneySelector onSelectJourney={setSelectedJourney} />;
    };

    useEvent({ eventName: 'close-application', onReceiveEvent: () => setSelectedJourney(null) });

    const [isCustomerTreeVisible, setCustomerTreeVisible] = useState(false);

    useEffect(() => {
        if (selectedJourney) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [selectedJourney]);

    const [menuState, setMenuState] = useState<{ isOpen: true; originNode: Element } | { isOpen: false }>({
        isOpen: false
    });

    const menuPositioning = (() => {
        if (!menuState.isOpen) return;

        const targetNode = menuState.originNode.getBoundingClientRect();
        return {
            $right: window.innerWidth - targetNode.right,
            $top: targetNode.bottom
        };
    })();

    const menuRef = useRef<HTMLDivElement>(null);

    useClickOutside(menuRef, () => {
        if (menuState.isOpen) {
            setMenuState({ isOpen: false });
        }
    });

    return (
        <>
            <MenuContainer {...menuPositioning} ref={menuRef}>
                <OverflowMenu
                    onClose={() => { }}
                    isOpen={menuState.isOpen}
                    onChange={(id) => {
                        if (id === 'logout') {
                            handleLogoutClick();
                        }

                        if (id === 'customer-tree') {
                            setCustomerTreeVisible(true);
                        }

                        setMenuState({ isOpen: false });
                    }}
                    onOutsideClick={() => { }}
                    srText=""
                >
                    <OverflowMenuItem value="customer-tree" text="Portfolio" />
                    <OverflowMenuItem value="logout" text="Logout" />
                </OverflowMenu>
            </MenuContainer>
            <CustomerTreeResponsiveTray
                isOpen={isCustomerTreeVisible}
                onClose={() => {
                    setCustomerTreeVisible(false);
                }}
            />
            <MainContainer className={auroraTheme}>
                <Header
                    onPressSubscriptionTypeButton={setSelectedSubscriptionType}
                    isAmigo={isAmigo}
                    login={{
                        loggedInInitials:
                            currentLoggedUser && isLoggedUser
                                ? `${currentLoggedUser.firstName?.[0]}${currentLoggedUser.lastName?.[0]}`
                                : undefined,
                        isLoggedIn: isLoggedUser,
                        onLogin: handleLoginClick,
                        onLogout: (event) => {
                            setMenuState({ isOpen: true, originNode: event.currentTarget });
                        }
                    }}
                />
                <ContentContainer isAmigo={isAmigo}>
                    {!selectedJourney && <Banner />}

                    <InnerContainer isMfeRunning={!!selectedJourney}>
                        {!selectedJourney && <LoggedUserInfo />}
                        {selectedJourney ? (
                            <JourneyLoader journey={selectedJourney} key={selectedJourney.id + refreshKey} />
                        ) : (
                            <>{renderSelector()}</>
                        )}
                    </InnerContainer>
                </ContentContainer>

                <Footer />
            </MainContainer>
        </>
    );
};

const App = () => {
    return (
        <ContentfulProvider>
            <ThemeProvider>
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <NotificationsProvider>
                        <LoadingProvider>
                            <ErrorProvider>
                                <AppContent />
                            </ErrorProvider>
                        </LoadingProvider>
                    </NotificationsProvider>
                </ErrorBoundary>
            </ThemeProvider>
        </ContentfulProvider>
    );
};

export default withProvider(App);
